import React, { useState, useEffect, useRef } from "react";
import { adminApi } from "../../../repositories/admin";
import Pagination from "../../../components/common/Pagination";
import { useDebounce } from "use-debounce";
import DataTable, { DataTableColumn, dataTableRowClass, dataTableThClass, dataTableTdClass } from "../../../components/ui/DataTable";
import { ArrowUp, ArrowDown, ChevronDown } from "lucide-react";
import { cn } from "../../../DashboardPage/lib/utils";
import Button from "../../../components/ui/Button";

interface UserTracking {
  id: number;
  user_name: string;
  label_name: string;
  account_created: string;
  last_login: string | null;
  no_of_artists: number;
  no_of_pitches: number;
  pitches_from_platform: number;
  pitches_from_api: number;
  pitches_about_to_release: number;
}

interface Pagination {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
}

interface Label {
  id: number;
  name: string;
}

const UserLevelTracking: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [users, setUsers] = useState<UserTracking[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 0,
  });
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [selectedLabel, setSelectedLabel] = useState<number | undefined>(undefined);
  const [labels, setLabels] = useState<Label[]>([]);
  const [sortField, setSortField] = useState<keyof UserTracking>("account_created");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 400);
  const requestIdRef = useRef(0);

  useEffect(() => {
    fetchLabels();
  }, []);

  useEffect(() => {
    fetchUserTracking();
  }, [pagination.page, pagination.limit, sortOrder, selectedLabel, debouncedSearchTerm]);

  const fetchLabels = async () => {
    try {
      const response = await adminApi.getLabels();
      if (response?.data?.success) {
        setLabels(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching labels:", error);
    }
  };

  const fetchUserTracking = async () => {
    const requestId = ++requestIdRef.current;
    try {
      setLoading(true);
      const response = await adminApi.getAllUserTracking({
        label: selectedLabel,
        sort: sortOrder,
        page: pagination.page,
        limit: pagination.limit,
        search: debouncedSearchTerm,
      });

      if (requestId !== requestIdRef.current) return;

      if (response?.data?.success) {
        setUsers(response.data.data || []);
        setPagination((prev) => ({
          ...prev,
          totalItems: response.data.pagination?.totalItems ?? prev.totalItems,
          totalPages: response.data.pagination?.totalPages ?? prev.totalPages,
        }));
      }
    } catch (error) {
      console.error("Error fetching user tracking:", error);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSort = (field: keyof UserTracking) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSortedUsers = () => {
    const sorted = [...users];
    sorted.sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      if (sortField === "account_created" || sortField === "last_login") {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  const exportToCSV = async () => {
    try {
      setExporting(true);

      const response = await adminApi.getAllUserTracking({
        label: selectedLabel,
        sort: sortOrder,
        page: 1,
        limit: 10000,
        search: searchTerm,
      });

      if (!response?.data?.success) {
        console.error("Failed to fetch data for export");
        return;
      }

      const allUsers: UserTracking[] = response.data.data || [];

      const headers = [
        "User Name",
        "Company",
        "Account Created",
        "Last Login",
        "Artists",
        "Pitches",
        "From Platform",
        "From API",
        "About to Release",
      ];

      const escapeCSV = (value: string | number | null): string => {
        const str = value === null ? "Never" : String(value);
        if (str.includes(",") || str.includes('"') || str.includes("\n")) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const rows = allUsers.map((user) =>
        [
          escapeCSV(user.user_name),
          escapeCSV(user.label_name),
          escapeCSV(formatDate(user.account_created)),
          escapeCSV(formatDate(user.last_login)),
          user.no_of_artists,
          user.no_of_pitches,
          user.pitches_from_platform,
          user.pitches_from_api,
          user.pitches_about_to_release,
        ].join(",")
      );

      const csvContent = [headers.join(","), ...rows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `user_tracking_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting CSV:", error);
    } finally {
      setExporting(false);
    }
  };

  if (loading && users.length === 0) {
  }

  // Header markup comes from DataTable's `head` prop (sortable headers and
  // multi-line cells); this list only sets the column count for its
  // loading and empty state rows.
  const userColumns: DataTableColumn<any>[] = [
    { key: "name", header: "" },
    { key: "email", header: "" },
    { key: "company", header: "" },
    { key: "createdAt", header: "" },
    { key: "lastLogin", header: "" },
    { key: "dspSetup", header: "" },
    { key: "pitches", header: "" },
    { key: "firstPitch", header: "" },
    { key: "secondPitch", header: "" },
  ];
  return (
    <div>
      {/* Filter Controls */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <label className="text-sm font-medium text-neutral-700">Filter by Company:</label>
            <div className="relative">
              <select
                value={selectedLabel || ""}
                onChange={(e) =>
                  setSelectedLabel(e.target.value ? Number(e.target.value) : undefined)
                }
                className="py-2 pl-3 pr-9 text-sm text-black transition-colors bg-white border rounded-md cursor-pointer appearance-none border-neutral-300 hover:border-primary-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              >
                <option value="">All Users</option>
                {labels.map((label) => (
                  <option key={label.id} value={label.id}>
                    {label.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-3 top-1/2 text-neutral-500" />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-neutral-700">Items per page:</label>
              <div className="relative">
                <select
                  value={pagination.limit}
                  onChange={(e) => handleLimitChange(Number(e.target.value))}
                  className="py-2 pl-3 pr-9 text-sm text-black transition-colors bg-white border rounded-md cursor-pointer appearance-none border-neutral-300 hover:border-primary-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <ChevronDown className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-3 top-1/2 text-neutral-500" />
              </div>
            </div>
            <Button
              onClick={exportToCSV}
              loading={exporting}
              disabled={users.length === 0}
            >
              {exporting ? "Exporting..." : "Export CSV"}
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <label className="text-sm font-medium text-neutral-700">Search by Name or Email:</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            placeholder="Search users..."
            className="px-3 py-2 text-sm text-black transition-colors bg-white border rounded-md cursor-pointer border-neutral-300 outline-none focus:border-primary-500"
          />
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={userColumns}
        loading={loading}
        skeletonRows={8}
        head={
          <thead>
          <tr className="border-b border-neutral-200 bg-primary-50">
          <th
          className="px-4 py-3 text-xs font-semibold tracking-wide text-left uppercase transition-colors cursor-pointer text-primary-900 hover:bg-primary-100"
          onClick={() => handleSort("user_name")}
          >
          <div className="flex items-center gap-2">
          User Name
          {sortField === "user_name" && (sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)}
          </div>
          </th>
          <th
          className="px-4 py-3 text-xs font-semibold tracking-wide text-left uppercase transition-colors cursor-pointer text-primary-900 hover:bg-primary-100"
          onClick={() => handleSort("label_name")}
          >
          <div className="flex items-center gap-2">
          Company
          {sortField === "label_name" && (sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)}
          </div>
          </th>
          <th
          className="px-4 py-3 text-xs font-semibold tracking-wide text-left uppercase transition-colors cursor-pointer text-primary-900 hover:bg-primary-100"
          onClick={() => handleSort("account_created")}
          >
          <div className="flex items-center gap-2">
          Account Created
          {sortField === "account_created" && (sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)}
          </div>
          </th>
          <th
          className="px-4 py-3 text-xs font-semibold tracking-wide text-left uppercase transition-colors cursor-pointer text-primary-900 hover:bg-primary-100"
          onClick={() => handleSort("last_login")}
          >
          <div className="flex items-center gap-2">
          Last Login
          {sortField === "last_login" && (sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)}
          </div>
          </th>
          <th
          className="px-4 py-3 text-xs font-semibold tracking-wide text-left uppercase transition-colors cursor-pointer text-primary-900 hover:bg-primary-100"
          onClick={() => handleSort("no_of_artists")}
          >
          <div className="flex items-center gap-2">
          Artists
          {sortField === "no_of_artists" && (sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)}
          </div>
          </th>
          <th
          className="px-4 py-3 text-xs font-semibold tracking-wide text-left uppercase transition-colors cursor-pointer text-primary-900 hover:bg-primary-100"
          onClick={() => handleSort("no_of_pitches")}
          >
          <div className="flex items-center gap-2">
          Pitches
          {sortField === "no_of_pitches" && (sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)}
          </div>
          </th>
          <th
          className="px-4 py-3 text-xs font-semibold tracking-wide text-left uppercase transition-colors cursor-pointer text-primary-900 hover:bg-primary-100"
          onClick={() => handleSort("pitches_from_platform")}
          >
          <div className="flex items-center gap-2">
          From Platform
          {sortField === "pitches_from_platform" && (sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)}
          </div>
          </th>
          <th
          className="px-4 py-3 text-xs font-semibold tracking-wide text-left uppercase transition-colors cursor-pointer text-primary-900 hover:bg-primary-100"
          onClick={() => handleSort("pitches_from_api")}
          >
          <div className="flex items-center gap-2">
          From API
          {sortField === "pitches_from_api" && (sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)}
          </div>
          </th>
          <th
          className="px-4 py-3 text-xs font-semibold tracking-wide text-left uppercase transition-colors cursor-pointer text-primary-900 hover:bg-primary-100"
          onClick={() => handleSort("pitches_about_to_release")}
          >
          <div className="flex items-center gap-2">
          About to Release
          {sortField === "pitches_about_to_release" && (sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)}
          </div>
          </th>
          </tr>
          </thead>
        }
      >
            {getSortedUsers().length > 0 ? (
              getSortedUsers().map((user) => (
                <tr key={user.id} className={dataTableRowClass}>
                  <td className={cn(dataTableTdClass, "font-medium")}>{user.user_name}</td>
                  <td className={cn(dataTableTdClass, "text-neutral-600")}>{user.label_name}</td>
                  <td className={cn(dataTableTdClass, "text-neutral-600")}>{formatDate(user.account_created)}</td>
                  <td className={cn(dataTableTdClass, "text-neutral-600")}>{formatDate(user.last_login)}</td>
                  <td className={cn(dataTableTdClass, "text-center tabular-nums")}>{user.no_of_artists}</td>
                  <td className={cn(dataTableTdClass, "text-center tabular-nums")}>{user.no_of_pitches}</td>
                  <td className={cn(dataTableTdClass, "text-center tabular-nums")}>{user.pitches_from_platform}</td>
                  <td className={cn(dataTableTdClass, "text-center tabular-nums")}>{user.pitches_from_api}</td>
                  <td className={cn(dataTableTdClass, "text-center tabular-nums")}>{user.pitches_about_to_release}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="px-4 py-10 text-sm text-center text-neutral-500" colSpan={9}>
                  No user data available.
                </td>
              </tr>
            )}
      </DataTable>

      {/* Pagination */}
      <div className="mt-4">
        <Pagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          setPage={handlePageChange}
          totalItems={pagination.totalItems}
          limit={pagination.limit}
        />
      </div>
    </div>
  );
};

export default UserLevelTracking;
